import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, FileSpreadsheet, CheckCircle2, Loader2, X, ClipboardCopy } from 'lucide-react';
import Modal from '../common/modals/Modal';
import { useTheme } from '../../context/ThemeContext';
import { apiUpload, apiGet, apiPost } from '../../utils/api';
import toast from 'react-hot-toast';

const AuditFileSelectionModal = ({ isOpen, onClose, property, onSelectionComplete }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const [step, setStep] = useState('upload'); // upload | selecting | review | confirming | done
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [selectionPct, setSelectionPct] = useState(20);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setStep('upload');
      setSelectedFile(null);
      setResult(null);
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleRun = async () => {
    if (!selectedFile || !property) return;
    setStep('selecting');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('property_name', property.property_name);
      formData.append('selection_pct', String(selectionPct));

      const response = await apiUpload(`/admin/audit_tracker/properties/${property.id}/file_selection`, formData);

      if (response.status === 202) {
        const data = await response.json();
        pollRef.current = setInterval(() => pollStatus(data.task_id), 2000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to start selection');
      }
    } catch (err) {
      toast.error(err.message);
      setStep('upload');
    }
  };

  const pollStatus = useCallback(async (tid) => {
    try {
      const response = await apiGet(`/admin/audit_tracker/properties/${property.id}/file_selection_status/${tid}`);
      if (!response.ok) return;

      const data = await response.json();
      if (data.status === 'done') {
        clearInterval(pollRef.current);
        pollRef.current = null;

        if (data.result?.success) {
          setResult(data.result);
          setStep('review');
        } else {
          toast.error(data.result?.error || 'Selection failed');
          setStep('upload');
        }
      }
    } catch {}
  }, [property]);

  const handleConfirm = async () => {
    if (!result?.selected_files) return;
    setStep('confirming');

    try {
      const response = await apiPost(`/admin/audit_tracker/properties/${property.id}/file_selection_confirm`, {
        selected_files: result.selected_files,
        formatted_list: result.formatted_list,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to confirm');
      }

      const data = await response.json();
      toast.success(data.message);
      setStep('done');
      if (onSelectionComplete) onSelectionComplete(data.property);
    } catch (err) {
      toast.error(err.message);
      setStep('review');
    }
  };

  const copyFormattedList = () => {
    if (result?.formatted_list) {
      navigator.clipboard.writeText(result.formatted_list);
      toast.success('Selection list copied to clipboard');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={step === 'selecting' || step === 'confirming' ? undefined : onClose}
      title={`File Selection — ${property?.property_name || ''}`}
      size="2xl"
      footer={
        step === 'upload' ? (
          <>
            <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
              Cancel
            </button>
            <button onClick={handleRun} disabled={!selectedFile} className="px-4 py-2 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
              Run Selection
            </button>
          </>
        ) : step === 'review' ? (
          <>
            <button onClick={copyFormattedList} className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
              <ClipboardCopy size={14} /> Copy List
            </button>
            <button onClick={() => setStep('upload')} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
              Re-run
            </button>
            <button onClick={handleConfirm} className="px-4 py-2 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white">
              Confirm Selection ({result?.selected_files?.length} files)
            </button>
          </>
        ) : step === 'done' ? (
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white">
            Done
          </button>
        ) : null
      }
    >
      <div className="min-h-[200px]">
        {/* Upload */}
        {step === 'upload' && (
          <div className="space-y-4">
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Upload the rent roll for this property. AI will parse the units and select {selectionPct}% for audit review.
            </p>

            <div className="flex items-center gap-3">
              <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Selection %</label>
              <input
                type="number"
                min={5}
                max={100}
                value={selectionPct}
                onChange={(e) => setSelectionPct(Math.max(5, Math.min(100, parseInt(e.target.value) || 20)))}
                className={`w-20 px-3 py-1.5 rounded-lg border text-sm ${
                  isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>

            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const file = e.dataTransfer.files?.[0];
                if (file) setSelectedFile(file);
              }}
              className={`flex items-center justify-center gap-3 p-8 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                dragging
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : isDark ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              {selectedFile ? (
                <div className="flex items-center gap-2">
                  <FileSpreadsheet size={20} className="text-green-500" />
                  <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{selectedFile.name}</span>
                  <button onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }} className="p-1 text-red-400 hover:text-red-500">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <Upload size={28} className={`mx-auto mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {dragging ? 'Drop rent roll here' : 'Drag & drop or click to select rent roll'}
                  </span>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
          </div>
        )}

        {/* Selecting */}
        {step === 'selecting' && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-blue-500 mb-4" />
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              AI is analyzing the rent roll and selecting files...
            </p>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Parsing units, applying certification logic, prioritizing selections
            </p>
          </div>
        )}

        {/* Review */}
        {step === 'review' && result && (
          <div className="space-y-4">
            {/* Summary */}
            <div className={`flex items-center gap-2 p-3 rounded-lg ${isDark ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
              <CheckCircle2 size={18} className="text-green-500" />
              <span className={`text-sm ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                Selected {result.selected_files?.length} of {result.affordable_units} affordable units ({result.selection_pct}%)
              </span>
            </div>

            {/* Stats */}
            <div className={`grid grid-cols-4 gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              {[
                { label: 'Total Units', value: result.total_units },
                { label: 'Affordable', value: result.affordable_units },
                { label: 'Market (excl.)', value: result.excluded_market },
                { label: 'Vacant (excl.)', value: result.excluded_vacant },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className={`text-lg font-bold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{s.value}</div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Selected files table */}
            <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0">
                  <tr className={`border-b ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                    {['Unit', 'Tenant', 'Type', 'Cert. Date', 'Building'].map((h) => (
                      <th key={h} className={`px-3 py-1.5 text-left font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.selected_files?.map((f, i) => (
                    <tr key={i} className={`border-b ${isDark ? 'border-gray-700/50' : 'border-gray-100'}`}>
                      <td className={`px-3 py-1.5 font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{f.unit_number}</td>
                      <td className={`px-3 py-1.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{f.tenant_name}</td>
                      <td className={`px-3 py-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          f.certification_type === 'Initial'
                            ? (isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700')
                            : (isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700')
                        }`}>
                          {f.certification_type}
                        </span>
                      </td>
                      <td className={`px-3 py-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{f.certification_date}</td>
                      <td className={`px-3 py-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{f.building || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Formatted list preview */}
            {result.formatted_list && (
              <div>
                <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Formatted Selection List (for email)</div>
                <pre className={`text-xs p-3 rounded-lg overflow-x-auto whitespace-pre ${
                  isDark ? 'bg-gray-900 text-gray-300 border border-gray-700' : 'bg-gray-50 text-gray-600 border border-gray-200'
                }`}>
                  {result.formatted_list}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Confirming */}
        {step === 'confirming' && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-blue-500 mb-4" />
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Saving selection...</p>
          </div>
        )}

        {/* Done */}
        {step === 'done' && (
          <div className={`text-center py-8`}>
            <CheckCircle2 size={36} className="text-green-500 mx-auto mb-3" />
            <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              {result?.selected_files?.length} files selected and recorded
            </p>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Tenant file records created. Property advanced to "Files Selected".
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AuditFileSelectionModal;
